import { useEffect, useState } from "react";
import { readToken } from "./authState";

/**
 * 자동 정밀 검토(2026-10-05) — 한글 서술형 검색어에서 PROVE 모델의 호와 앞단 LLM 후보 1순위의 호가 엇갈리면
 * 정밀 모델에게 한 번 묻는다(서버 /api/onto/proposal/precise-check, 24시간 기억). 960문항 실측: 이 조건에서만 부르면
 * 시험형 65→88%, 결정례·신고는 손해 0, 호출은 검색의 약 6%. 판정이 아니라 출처별 세번의 한 줄('정밀 검토').
 */

/** 한글이 영문보다 많고 신고서 칸 모양이 아니다(서버와 같은 규칙). */
export function koreanDescriptive(q) {
  const s = String(q || "");
  if (!s.trim()) return false;
  let hangul = 0;
  let latin = 0;
  for (const ch of s) {
    const c = ch.codePointAt(0);
    if (c >= 0xac00 && c <= 0xd7a3) hangul += 1;
    else if (/[a-z]/i.test(ch)) latin += 1;
  }
  return hangul > latin && !s.includes("거래품명:") && !s.includes("통관:");
}

/** PROVE 모델의 호·LLM 후보 1순위의 호 — 둘 다 있고 다를 때만 정밀 검토. */
export function shouldPreciseCheck(q, precedentHints, proposal) {
  if (!koreanDescriptive(q)) return false;
  const prove = String(precedentHints?.model_proposal?.hsk || "").replace(/\D/g, "").slice(0, 4);
  const cands = proposal?.signals?.llm?.candidates;
  const llm = Array.isArray(cands) && cands.length ? String(cands[0]?.code || "").replace(/\D/g, "").slice(0, 4) : "";
  return prove.length === 4 && llm.length === 4 && prove !== llm;
}

const memo = new Map();   // 같은 탭에서 같은 검색어는 한 번만

/** { status: "idle"|"pending"|"ok"|"skipped"|"unavailable"|"limit", hs4?, reason? } */
export function usePreciseCheck(q, precedentHints, proposal, fetchImpl = null) {
  const want = shouldPreciseCheck(q, precedentHints, proposal);
  const key = String(q || "").replace(/\s+/g, " ").trim();
  const [state, setState] = useState(() => (want && memo.has(key) ? memo.get(key) : { status: "idle" }));
  useEffect(() => {
    if (!want) { setState({ status: "idle" }); return undefined; }
    if (memo.has(key) && memo.get(key).status !== "pending") { setState(memo.get(key)); return undefined; }
    const token = readToken();
    if (!token) { setState({ status: "idle" }); return undefined; }
    let alive = true;
    setState({ status: "pending" });
    memo.set(key, { status: "pending" });
    (fetchImpl || fetch)("/api/onto/proposal/precise-check", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ query: key }),
    })
      .then((r) => (r && r.ok ? r.json() : { status: "unavailable" }))
      .catch(() => ({ status: "unavailable" }))
      .then((res) => {
        const v = res && res.status === "ok" && String(res.hs4 || "").length === 4
          ? { status: "ok", hs4: String(res.hs4), reason: String(res.reason || "") }
          : { status: (res && res.status) || "unavailable" };
        if (v.status === "ok" || v.status === "skipped") memo.set(key, v); else memo.delete(key);
        if (alive) setState(v);
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [want, key]);
  return want ? state : { status: "idle" };
}

export function __resetPreciseMemoForTest() {
  memo.clear();
}
